/**
 * Tenancy Admin Console (Super-Admin only).
 *
 * READ-ONLY by design except for two safe, idempotent actions:
 *   1. Run Backfill   — adds tenant_id=tnt_default where missing (never overwrites)
 *   2. Run Reconciliation — purely read-only coverage check
 *   3. Run Isolation Tests — runs pytest in subprocess, stores last result
 *
 * NO destructive controls are exposed:
 *   - tenancy.enforced toggle is NOT available
 *   - feature flags are READ-ONLY
 *   - tenants list is READ-ONLY
 *   - no Delete / Edit / Migrate-Anything buttons
 *
 * Route: /platform/tenancy   (mounted in App.js, admin-only via ProtectedRoute + role check)
 */
import React, { useEffect, useState, useMemo } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import {
  ShieldCheck, Layers, Database, ToggleLeft, CheckCircle2, XCircle,
  RefreshCw, Activity, Eye, AlertTriangle, ServerCog, BadgeCheck, Lock,
  GitBranch, Hourglass, ArrowRight,
} from "lucide-react";

const Badge = ({ tone = "stone", children, testId }) => {
  const tones = {
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    red: "bg-rose-50 text-rose-700 border-rose-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    blue: "bg-sky-50 text-sky-700 border-sky-200",
    stone: "bg-stone-50 text-stone-700 border-stone-200",
    gold: "bg-[#FCF4E5] text-[#8a6418] border-[#E8D29C]",
  };
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${tones[tone] || tones.stone}`}>
      {children}
    </span>
  );
};

const Stat = ({ label, value, sub, icon: Icon, tone = "stone", testId }) => (
  <div data-testid={testId} className="bg-white rounded-xl border border-stone-200 p-4 flex items-start gap-3">
    {Icon && <div className={`p-2 rounded-lg bg-${tone}-50 text-${tone}-700`}><Icon size={18} /></div>}
    <div className="flex-1 min-w-0">
      <div className="text-[11px] uppercase tracking-wider text-stone-500">{label}</div>
      <div className="text-2xl font-bold text-stone-800 mt-0.5">{value}</div>
      {sub && <div className="text-xs text-stone-500 mt-1">{sub}</div>}
    </div>
  </div>
);

const SectionCard = ({ title, icon: Icon, action, children, testId }) => (
  <div data-testid={testId} className="bg-white rounded-xl border border-stone-200 overflow-hidden">
    <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between bg-stone-50/50">
      <div className="flex items-center gap-2 text-stone-700 font-semibold">
        {Icon && <Icon size={16} className="text-[#7A1F3D]" />}
        <span>{title}</span>
      </div>
      {action}
    </div>
    <div>{children}</div>
  </div>
);

const ConfirmModal = ({ open, title, description, danger, onConfirm, onCancel, busy }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="tenancy-confirm-modal" onMouseDown={(e) => onBackdropMouseDown(e, onCancel)}>
      <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl" onMouseDown={onModalPanelMouseDown}>
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${danger ? "bg-amber-50 text-amber-700" : "bg-sky-50 text-sky-700"}`}>
            {danger ? <AlertTriangle size={20} /> : <BadgeCheck size={20} />}
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-stone-800">{title}</h3>
            <p className="text-sm text-stone-600 mt-2 leading-relaxed">{description}</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button data-testid="tenancy-confirm-cancel" onClick={onCancel} disabled={busy} className="px-4 py-2 text-sm rounded-lg border border-stone-200 hover:bg-stone-50 disabled:opacity-50">إلغاء</button>
          <button data-testid="tenancy-confirm-ok" onClick={onConfirm} disabled={busy} className="px-4 py-2 text-sm rounded-lg bg-gradient-to-r from-[#7A1F3D] to-[#9B2950] text-white hover:opacity-95 disabled:opacity-50 inline-flex items-center gap-2">
            {busy && <RefreshCw size={14} className="animate-spin" />} تأكيد
          </button>
        </div>
      </div>
    </div>
  );
};

export default function TenancyAdmin() {
  const { user, loading: authLoading } = useAuth();

  const [status, setStatus] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [indexes, setIndexes] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [flags, setFlags] = useState([]);
  const [lastTest, setLastTest] = useState(null);
  const [loading, setLoading] = useState(true);

  const [confirmAction, setConfirmAction] = useState(null); // { kind, run() }
  const [busy, setBusy] = useState(false);

  const isAdmin = user && user.role === "admin";

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, c, idx, t, f, lt] = await Promise.all([
        api.get("/tenancy/status"),
        api.get("/tenancy/coverage"),
        api.get("/tenancy/indexes"),
        api.get("/tenancy/tenants"),
        api.get("/tenancy/flags"),
        api.get("/tenancy/test-results"),
      ]);
      setStatus(s.data);
      setCoverage(c.data);
      setIndexes(idx.data);
      setTenants(t.data.tenants || []);
      setFlags(f.data.flags || []);
      setLastTest(lt.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر تحميل البيانات");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) loadAll();
  }, [isAdmin]);

  const lowestCoverage = useMemo(() => {
    if (!coverage?.collections) return null;
    return coverage.collections.reduce((min, c) =>
      (c.coverage_pct ?? 100) < (min?.coverage_pct ?? 100) ? c : min, null);
  }, [coverage]);

  // ============ Mini Wizard: Expand → Migrate → Contract ============
  const wizardPhases = useMemo(() => {
    const overall = coverage?.summary?.overall_coverage_pct ?? 0;
    const defaultPresent = !!status?.default_tenant;
    const enforced = !!status?.enforced;
    const dualWrite = !!status?.dual_write;
    const testsOk = lastTest?.available && lastTest?.result?.ok && (lastTest?.result?.failed ?? 1) === 0;
    const testsPassed = lastTest?.result?.passed ?? 0;
    // count compound tenant-first indexes
    const tenantFirstIdx = (indexes?.collections || []).reduce((s, c) => s + (c.tenant_first_count || 0), 0);

    return [
      {
        id: "expand",
        title: "Expand",
        subtitle: "إضافة tenant_id بشكل non-breaking",
        checks: [
          { label: "وجود tenant افتراضي (tnt_default)", pass: defaultPresent },
          { label: "تغطية tenant_id = 100% في كل الـ collections", pass: overall === 100 },
          { label: "وجود compound indexes تبدأ بـ tenant_id", pass: tenantFirstIdx >= 10, detail: `${tenantFirstIdx} index` },
          { label: "آخر Reconciliation نجح", pass: overall === 100 && defaultPresent },
        ],
        risks: overall < 100 ? ["لا يجوز الانتقال إلى Migrate قبل إكمال التغطية"] : [],
      },
      {
        id: "migrate",
        title: "Migrate",
        subtitle: "تحويل القراءات لاستخدام TenantContext خلف flag",
        checks: [
          { label: "كل شروط Expand نجحت", pass: overall === 100 && defaultPresent },
          { label: "Isolation Tests Pass (آخر تشغيل)", pass: testsOk, detail: testsOk ? `${testsPassed} passed` : "لم يُشغّل أو فشل" },
          { label: "tenancy.dual_write مفعّل", pass: dualWrite, detail: "يُفعَّل في Phase 2 فقط — لا داعي الآن" },
          { label: "مشغّل على staging لمدة 7 أيام", pass: false, detail: "خطوة يدوية لاحقاً" },
        ],
        risks: [
          "لا تفعّل tenancy.enforced قبل تشغيل suite متكاملة من cross-tenant probes",
          "أي endpoint غير محقون بـ TenantContext قد يكسر",
        ],
      },
      {
        id: "contract",
        title: "Contract",
        subtitle: "حذف legacy paths وجعل tenant_id إلزامياً",
        checks: [
          { label: "tenancy.enforced=ON على 100% من المرور لمدة 7 أيام", pass: false, detail: "متاحة بعد Migrate" },
          { label: "لا أي endpoint legacy فاعل", pass: false, detail: "يتطلب refactor كامل لـ server.py" },
          { label: "Mongo snapshot أُخذ قبل الـ Contract", pass: false, detail: "خطوة عمليات يدوية" },
          { label: "Rollback drill على staging", pass: false, detail: "إجباري قبل Production" },
        ],
        risks: [
          "غير قابلة للتنفيذ من هذه الواجهة — تتطلب موافقة Super-Admin + Code release موقّع",
          "كل خطوة هنا لها rollback script مستقل في scripts/migrate/",
        ],
      },
    ];
  }, [coverage, status, lastTest, indexes]);

  const phaseStatus = (p) => {
    const total = p.checks.length;
    const passed = p.checks.filter((c) => c.pass).length;
    if (passed === total) return { label: "Pass", tone: "green", icon: CheckCircle2 };
    if (passed === 0) return { label: "Not started", tone: "stone", icon: Hourglass };
    return { label: `Blocked (${passed}/${total})`, tone: "amber", icon: AlertTriangle };
  };

  if (authLoading) return <div className="min-h-screen flex items-center justify-center text-stone-500">جاري التحميل…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center max-w-md" data-testid="tenancy-forbidden">
          <Lock className="mx-auto text-rose-500 mb-3" size={36} />
          <h2 className="text-xl font-bold text-stone-800">مخصّص لـ Super Admin</h2>
          <p className="text-sm text-stone-500 mt-2">هذه الصفحة متاحة لمسؤولي المنصة فقط.</p>
        </div>
      </div>
    );
  }

  const flagBadgeTone = (state, def) => {
    if (state === "ga" || def === true) return "green";
    if (state === "beta") return "amber";
    if (state === "deprecated") return "red";
    return "stone";
  };

  const askConfirm = (kind, runFn, title, description, danger = false) => {
    setConfirmAction({ kind, run: runFn, title, description, danger });
  };

  const runWith = async (path) => {
    setBusy(true);
    try {
      const r = await api.post(path, null, { headers: { "X-Confirm-Action": "true" } });
      toast.success("تم التنفيذ بنجاح");
      await loadAll();
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل التنفيذ");
    } finally {
      setBusy(false);
      setConfirmAction(null);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="tenancy-admin-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-[11px] tracking-widest uppercase text-stone-500">
            <ShieldCheck size={14} className="text-[#7A1F3D]" /> Platform · Tenancy
          </div>
          <h1 className="text-2xl font-bold text-stone-800 mt-1">إدارة الـ Tenancy</h1>
          <p className="text-sm text-stone-500 mt-1">واجهة قراءة وعمليات آمنة فقط — لا تحوي تفعيل enforced ولا أي زر مدمر.</p>
        </div>
        <button data-testid="tenancy-refresh-btn" onClick={loadAll} disabled={loading} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-stone-200 bg-white hover:border-[#7A1F3D] text-sm text-stone-700 disabled:opacity-60">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> تحديث
        </button>
      </div>

      {/* Top stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat testId="stat-phase" label="Phase" value={status?.phase || "—"} icon={Activity} tone="amber" sub="مرحلة Expand — أمنة" />
        <Stat
          testId="stat-enforced"
          label="tenancy.enforced"
          value={status?.enforced ? "ON" : "OFF"}
          sub={status?.enforced ? "يطبّق scoping" : "السلوك القديم مستمر"}
          icon={ToggleLeft}
          tone={status?.enforced ? "green" : "stone"}
        />
        <Stat
          testId="stat-coverage"
          label="Overall coverage"
          value={`${coverage?.summary?.overall_coverage_pct ?? "—"}%`}
          sub={`${coverage?.summary?.docs_with_tenant_id ?? 0} / ${coverage?.summary?.docs_total ?? 0} doc`}
          icon={Database}
          tone={coverage?.summary?.overall_coverage_pct === 100 ? "green" : "amber"}
        />
        <Stat
          testId="stat-tenants"
          label="Tenants"
          value={tenants.length}
          sub={`الافتراضي: ${status?.default_tenant?.tenant_id || "—"}`}
          icon={Layers}
          tone="blue"
        />
      </div>

      {/* Safe actions row */}
      <SectionCard title="عمليات آمنة (إعادة تشغيل آمنة فقط)" icon={ServerCog} testId="tenancy-safe-actions">
        <div className="p-4 grid sm:grid-cols-3 gap-3">
          <button
            data-testid="tenancy-action-reconcile"
            disabled={busy}
            onClick={() => askConfirm(
              "reconcile",
              () => runWith("/tenancy/reconcile/run"),
              "إعادة تشغيل Reconciliation",
              "إجراء قراءة فقط — يفحص نسبة tenant_id على كل collection ولا يكتب أي شيء.",
              false,
            )}
            className="text-start p-4 rounded-lg border border-stone-200 hover:border-[#7A1F3D] hover:bg-stone-50 transition disabled:opacity-50"
          >
            <div className="flex items-center gap-2 text-stone-800 font-semibold"><Eye size={16} /> Reconciliation</div>
            <div className="text-xs text-stone-500 mt-1">قراءة فقط · يتحقق من تغطية tenant_id</div>
          </button>

          <button
            data-testid="tenancy-action-backfill"
            disabled={busy}
            onClick={() => askConfirm(
              "backfill",
              () => runWith("/tenancy/backfill/run"),
              "إعادة تشغيل Backfill",
              "عملية idempotent — يضيف tenant_id=tnt_default فقط للوثائق التي لا تملك قيمة. لا يكتب فوق أي قيمة موجودة ولا يحذف شيئاً.",
              false,
            )}
            className="text-start p-4 rounded-lg border border-stone-200 hover:border-[#7A1F3D] hover:bg-stone-50 transition disabled:opacity-50"
          >
            <div className="flex items-center gap-2 text-stone-800 font-semibold"><Database size={16} /> Backfill</div>
            <div className="text-xs text-stone-500 mt-1">آمن · لا يكتب فوق أي tenant_id موجود</div>
          </button>

          <button
            data-testid="tenancy-action-tests"
            disabled={busy}
            onClick={() => askConfirm(
              "tests",
              () => runWith("/tenancy/tests/run"),
              "تشغيل اختبارات Tenant Isolation",
              "يشغّل suite pytest الخاص بـ Phase 1 ويخزّن آخر نتيجة. لا يلامس أي بيانات إنتاج.",
              false,
            )}
            className="text-start p-4 rounded-lg border border-stone-200 hover:border-[#7A1F3D] hover:bg-stone-50 transition disabled:opacity-50"
          >
            <div className="flex items-center gap-2 text-stone-800 font-semibold"><BadgeCheck size={16} /> Isolation Tests</div>
            <div className="text-xs text-stone-500 mt-1">آمن · يكتب نتيجة في ملف فقط</div>
          </button>
        </div>
        <div className="px-4 pb-4 text-[11px] text-stone-500 flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 text-amber-500 flex-shrink-0" />
          <span>كل عملية تتطلب تأكيداً وتُرسل header <code>X-Confirm-Action: true</code>. لا توجد عمليات حذف أو تعديل tenant_id أو تفعيل enforced من هذه الواجهة.</span>
        </div>
      </SectionCard>

      {/* Mini Wizard — Expand → Migrate → Contract (read-only, guidance-first) */}
      <div className="mt-4">
        <SectionCard title="مراحل الترحيل — دليل" icon={GitBranch} testId="tenancy-wizard">
          <div className="p-4 grid lg:grid-cols-3 gap-3 relative">
            {wizardPhases.map((p, idx) => {
              const s = phaseStatus(p);
              const Icon = s.icon;
              return (
                <div key={p.id} className="relative" data-testid={`wizard-phase-${p.id}`}>
                  <div className={`rounded-lg border-2 p-4 h-full ${
                    s.tone === "green" ? "border-emerald-200 bg-emerald-50/30" :
                    s.tone === "amber" ? "border-amber-200 bg-amber-50/30" :
                    "border-stone-200 bg-stone-50/30"
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-stone-500">Phase {idx + 1}</div>
                        <div className="text-lg font-bold text-stone-800">{p.title}</div>
                        <div className="text-xs text-stone-600">{p.subtitle}</div>
                      </div>
                      <Badge tone={s.tone} testId={`wizard-status-${p.id}`}>
                        <Icon size={11} /> {s.label}
                      </Badge>
                    </div>

                    <div className="mt-3 space-y-1.5">
                      {p.checks.map((c, i) => (
                        <div key={i} className="flex items-start gap-2 text-[12px]">
                          {c.pass ? <CheckCircle2 size={13} className="text-emerald-600 mt-0.5 flex-shrink-0" /> : <XCircle size={13} className="text-stone-400 mt-0.5 flex-shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <div className={c.pass ? "text-stone-700" : "text-stone-500"}>{c.label}</div>
                            {c.detail && <div className="text-[10px] text-stone-500">{c.detail}</div>}
                          </div>
                        </div>
                      ))}
                    </div>

                    {p.risks.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-stone-200 space-y-1">
                        {p.risks.map((r, i) => (
                          <div key={i} className="flex items-start gap-1.5 text-[11px] text-amber-700">
                            <AlertTriangle size={11} className="mt-0.5 flex-shrink-0" />
                            <span>{r}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {idx < wizardPhases.length - 1 && (
                    <ArrowRight size={20} className="hidden lg:block absolute -end-3 top-1/2 -translate-y-1/2 text-stone-300" />
                  )}
                </div>
              );
            })}
          </div>
          <div className="px-4 pb-4 text-[11px] text-stone-500 flex items-start gap-2 border-t border-stone-100 pt-3">
            <ShieldCheck size={14} className="mt-0.5 text-emerald-600 flex-shrink-0" />
            <span>هذه نظرة إرشادية فقط. لا توجد أزرار هنا لتفعيل enforced أو حذف legacy. كل انتقال بين المراحل يتطلب release موقّع وموافقة Super-Admin.</span>
          </div>
        </SectionCard>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        {/* Tenants */}
        <SectionCard title="Tenants" icon={Layers} testId="tenancy-tenants-section">
          <div className="divide-y divide-stone-100">
            {tenants.length === 0 && <div className="p-4 text-sm text-stone-500">لا يوجد tenants.</div>}
            {tenants.map((t) => (
              <div key={t.tenant_id} className="p-4 flex items-center justify-between gap-3" data-testid={`tenant-row-${t.tenant_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-stone-800">{t.name?.ar || t.name?.en || t.slug}</span>
                    <Badge tone="gold">{t.tenant_id}</Badge>
                    <Badge tone="stone">{t.slug}</Badge>
                  </div>
                  <div className="text-xs text-stone-500 mt-1 flex items-center gap-3 flex-wrap">
                    <span>vertical: <strong>{t.vertical}</strong></span>
                    <span>plan: <strong>{t.plan}</strong></span>
                    <span>isolation: <strong>{t.isolation_mode}</strong></span>
                    <span>locale: <strong>{t.locale_default}</strong></span>
                  </div>
                </div>
                <Badge tone={t.status === "active" ? "green" : "amber"} testId={`tenant-status-${t.tenant_id}`}>
                  {t.status}
                </Badge>
              </div>
            ))}
          </div>
        </SectionCard>

        {/* Feature flags */}
        <SectionCard title="Feature Flags (read-only)" icon={ToggleLeft} testId="tenancy-flags-section">
          <div className="divide-y divide-stone-100 max-h-[420px] overflow-y-auto">
            {flags.map((f) => (
              <div key={f.flag_id} className="p-3 flex items-start justify-between gap-3" data-testid={`flag-row-${f.flag_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="text-[12px] font-mono text-stone-800">{f.flag_id}</code>
                    <Badge tone={flagBadgeTone(f.state, f.default_value)}>{f.state}</Badge>
                    <Badge tone={f.default_value ? "green" : "stone"}>default: {String(f.default_value)}</Badge>
                  </div>
                  {f.description && <div className="text-[11px] text-stone-500 mt-1">{f.description}</div>}
                </div>
              </div>
            ))}
            {flags.length === 0 && <div className="p-4 text-sm text-stone-500">لا توجد flags.</div>}
          </div>
        </SectionCard>
      </div>

      {/* Coverage table */}
      <div className="mt-4">
        <SectionCard
          title={`Tenant ID Coverage (${coverage?.collections?.length || 0} collections)`}
          icon={Database}
          testId="tenancy-coverage-section"
          action={
            lowestCoverage && lowestCoverage.coverage_pct < 100 ? (
              <Badge tone="amber">أدنى تغطية: {lowestCoverage.collection} {lowestCoverage.coverage_pct}%</Badge>
            ) : (
              <Badge tone="green" testId="coverage-all-ok"><CheckCircle2 size={12} /> الكل 100%</Badge>
            )
          }
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-stone-500 bg-stone-50">
                  <th className="text-start px-4 py-2 font-medium">Collection</th>
                  <th className="text-end px-4 py-2 font-medium">Total</th>
                  <th className="text-end px-4 py-2 font-medium">With tenant_id</th>
                  <th className="text-end px-4 py-2 font-medium">Missing</th>
                  <th className="text-end px-4 py-2 font-medium">%</th>
                  <th className="px-4 py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {coverage?.collections?.map((c) => {
                  const pct = c.coverage_pct ?? 0;
                  return (
                    <tr key={c.collection} className="border-t border-stone-100" data-testid={`coverage-row-${c.collection}`}>
                      <td className="px-4 py-2 font-mono text-[12px] text-stone-700">{c.collection}</td>
                      <td className="px-4 py-2 text-end text-stone-700">{c.total ?? 0}</td>
                      <td className="px-4 py-2 text-end text-stone-700">{c.with_tenant_id ?? 0}</td>
                      <td className={`px-4 py-2 text-end ${(c.without_tenant_id ?? 0) > 0 ? "text-rose-600 font-semibold" : "text-stone-700"}`}>{c.without_tenant_id ?? 0}</td>
                      <td className="px-4 py-2 text-end">
                        <span className={pct === 100 ? "text-emerald-700 font-semibold" : "text-amber-700 font-semibold"}>{pct}%</span>
                      </td>
                      <td className="px-4 py-2">
                        {c.error ? (
                          <Badge tone="red"><XCircle size={11} /> error</Badge>
                        ) : pct === 100 ? (
                          <Badge tone="green"><CheckCircle2 size={11} /> ok</Badge>
                        ) : (
                          <Badge tone="amber"><AlertTriangle size={11} /> incomplete</Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </SectionCard>
      </div>

      {/* Indexes */}
      <div className="mt-4">
        <SectionCard title="Indexes (per collection)" icon={Layers} testId="tenancy-indexes-section">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-stone-500 bg-stone-50">
                  <th className="text-start px-4 py-2 font-medium">Collection</th>
                  <th className="text-end px-4 py-2 font-medium">Index count</th>
                  <th className="text-end px-4 py-2 font-medium">Tenant-first</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {indexes?.collections?.map((c) => (
                  <tr key={c.collection} className="border-t border-stone-100" data-testid={`index-row-${c.collection}`}>
                    <td className="px-4 py-2 font-mono text-[12px] text-stone-700">{c.collection}</td>
                    <td className="px-4 py-2 text-end text-stone-700">{c.index_count ?? "—"}</td>
                    <td className="px-4 py-2 text-end text-stone-700">{c.tenant_first_count ?? "—"}</td>
                    <td className="px-4 py-2">
                      {c.error ? (
                        <Badge tone="red">error</Badge>
                      ) : (c.tenant_first_count ?? 0) > 0 ? (
                        <Badge tone="green"><CheckCircle2 size={11} /> compound</Badge>
                      ) : (
                        <Badge tone="amber">no tenant-first index</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      </div>

      {/* Last test results */}
      <div className="mt-4">
        <SectionCard title="آخر نتيجة Isolation Tests" icon={BadgeCheck} testId="tenancy-tests-section">
          {!lastTest?.available ? (
            <div className="p-5 text-sm text-stone-500">لم يُسجَّل تشغيل اختبارات بعد. اضغط زر "Isolation Tests" بالأعلى لتشغيلها.</div>
          ) : (
            <div className="p-4 space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <Badge tone={lastTest.result?.ok ? "green" : "red"} testId="last-test-status">
                  {lastTest.result?.ok ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
                  {lastTest.result?.ok ? "PASS" : "FAIL"}
                </Badge>
                <Badge tone="green">Passed: {lastTest.result?.passed ?? 0}</Badge>
                <Badge tone={lastTest.result?.failed ? "red" : "stone"}>Failed: {lastTest.result?.failed ?? 0}</Badge>
                <span className="text-xs text-stone-500">{lastTest.result?.ran_at}</span>
              </div>
              <pre className="text-[11px] bg-stone-900 text-stone-100 rounded-lg p-3 overflow-x-auto max-h-72" data-testid="last-test-tail">
                {lastTest.result?.tail || ""}
              </pre>
            </div>
          )}
        </SectionCard>
      </div>

      {/* Phase 4: Flag Management (Super-Admin only, whitelist-protected) */}
      <div className="mt-4">
        <FlagManagementSection flags={flags} loadAll={loadAll} />
      </div>

      <ConfirmModal
        open={!!confirmAction}
        title={confirmAction?.title}
        description={confirmAction?.description}
        danger={confirmAction?.danger}
        busy={busy}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() => confirmAction?.run?.()}
      />
    </div>
  );
}

// ============ Flag Management (Phase 4) ============
function FlagManagementSection({ flags, loadAll }) {
  const [togglable, setTogglable] = useState({ togglable: [], locked: [] });
  const [audit, setAudit] = useState([]);
  const [pending, setPending] = useState(null); // { flag_id, state, default_value }
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [t, a] = await Promise.all([
        api.get("/tenancy/flags/togglable"),
        api.get("/tenancy/audit/flags"),
      ]);
      setTogglable(t.data);
      setAudit(a.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر تحميل البيانات");
    }
  };

  useEffect(() => { load(); }, []);

  const flagByID = (id) => flags.find((f) => f.flag_id === id);

  const requestToggle = (flag_id, newState) => {
    setPending({
      flag_id,
      state: newState,
      default_value: newState === "ga",
      kind: "set",
      impact: impactOfFlag(flag_id, newState),
    });
  };

  const requestRollback = (flag_id) => {
    setPending({ flag_id, kind: "rollback", impact: "إيقاف فوري وآمن للميزة. لا يحذف أي بيانات." });
  };

  const confirm = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      if (pending.kind === "rollback") {
        await api.post(`/tenancy/flags/${pending.flag_id}/rollback`, null, { headers: { "X-Confirm-Action": "true" } });
        toast.success("تم rollback");
      } else {
        await api.put(`/tenancy/flags/${pending.flag_id}`,
          { state: pending.state, default_value: pending.default_value },
          { headers: { "X-Confirm-Action": "true" } });
        toast.success("تم تحديث الـ flag");
      }
      await Promise.all([load(), loadAll()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل");
    } finally {
      setBusy(false);
      setPending(null);
    }
  };

  return (
    <SectionCard title="Feature Flag Management (Super-Admin)" icon={ToggleLeft} testId="flag-mgmt-section">
      <div className="p-4 grid lg:grid-cols-3 gap-3">
        {togglable.togglable.map((fid) => {
          const f = flagByID(fid) || { state: "off", default_value: false };
          const on = f.state === "ga" || f.default_value === true;
          return (
            <div key={fid} className="rounded-lg border border-stone-200 p-3" data-testid={`flag-card-${fid}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <code className="text-[11px] font-mono text-stone-700 break-all">{fid}</code>
                  <div className="flex items-center gap-1 mt-1">
                    <Badge tone={on ? "green" : "stone"}>{f.state}</Badge>
                    {f.default_value && <Badge tone="green">default ON</Badge>}
                  </div>
                  {f.description && <div className="text-[11px] text-stone-500 mt-2 leading-relaxed">{f.description}</div>}
                </div>
              </div>
              <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                {!on && (
                  <button data-testid={`flag-enable-${fid}`} onClick={() => requestToggle(fid, "ga")} className="text-[11px] px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700">
                    تفعيل (ga)
                  </button>
                )}
                {on && (
                  <>
                    <button data-testid={`flag-beta-${fid}`} onClick={() => requestToggle(fid, "beta")} className="text-[11px] px-2 py-1 rounded bg-amber-500 text-white hover:bg-amber-600">
                      تحويل إلى beta
                    </button>
                    <button data-testid={`flag-rollback-${fid}`} onClick={() => requestRollback(fid)} className="text-[11px] px-2 py-1 rounded bg-rose-600 text-white hover:bg-rose-700 inline-flex items-center gap-1">
                      <RefreshCw size={10} /> Rollback OFF
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Locked flags */}
      <div className="px-4 pb-3">
        <div className="text-[11px] text-stone-500 mb-1.5">Flags مقفلة (تحتاج release ops):</div>
        <div className="flex flex-wrap gap-1.5">
          {togglable.locked.map((fid) => (
            <span key={fid} className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
              {fid}
            </span>
          ))}
        </div>
      </div>

      {/* Audit log */}
      <div className="border-t border-stone-100 p-4">
        <div className="text-xs font-semibold text-stone-700 mb-2 flex items-center gap-2">
          <BadgeCheck size={13} className="text-[#7A1F3D]" /> سجل التدقيق (آخر تغييرات flags)
        </div>
        <div className="max-h-56 overflow-y-auto text-[11px] space-y-1">
          {audit.length === 0 ? <div className="text-stone-400 text-center py-3">لا توجد تغييرات بعد.</div> :
            audit.map((a) => (
              <div key={a.audit_id} className="p-2 rounded bg-stone-50 border border-stone-100" data-testid={`audit-row-${a.audit_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Badge tone={a.action === "platform.flag_rollback" ? "amber" : "blue"}>{a.action.split(".").pop()}</Badge>
                    <code className="font-mono">{a.resource?.id}</code>
                  </div>
                  <span className="text-stone-500">{a.actor?.username} · {a.ts?.slice(0, 19)}</span>
                </div>
                {a.diff && (
                  <div className="mt-1 text-[10px] text-stone-600">
                    {Object.entries(a.diff).map(([k, v]) => (
                      <span key={k} className="me-2"><strong>{k}</strong>: {String(v.from)} → {String(v.to)}</span>
                    ))}
                  </div>
                )}
              </div>
            ))
          }
        </div>
      </div>

      {pending && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="flag-confirm-modal" onMouseDown={(e) => onBackdropMouseDown(e, () => setPending(null))}>
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl" onMouseDown={onModalPanelMouseDown}>
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-amber-50 text-amber-700">
                <AlertTriangle size={20} />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-stone-800">تأكيد تغيير flag حساس</h3>
                <p className="text-sm text-stone-600 mt-2 leading-relaxed">
                  <code className="bg-stone-100 px-1.5 py-0.5 rounded">{pending.flag_id}</code>
                </p>
                <div className="text-sm text-stone-600 mt-2">
                  {pending.kind === "rollback" ? "العملية: Rollback → OFF" :
                    <>العملية: تحويل إلى state <strong>{pending.state}</strong></>}
                </div>
                {pending.impact && (
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded text-[12px] text-amber-800" data-testid="flag-impact">
                    <strong>الأثر المتوقع:</strong> {pending.impact}
                  </div>
                )}
                <div className="mt-3 text-[11px] text-stone-500">سيُسجَّل التغيير في audit log باسمك وعنوان IP.</div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button data-testid="flag-cancel" onClick={() => setPending(null)} disabled={busy} className="px-4 py-2 text-sm rounded-lg border border-stone-200 hover:bg-stone-50">إلغاء</button>
              <button data-testid="flag-confirm" onClick={confirm} disabled={busy} className="px-4 py-2 text-sm rounded-lg bg-gradient-to-r from-[#7A1F3D] to-[#9B2950] text-white inline-flex items-center gap-2">
                {busy && <RefreshCw size={14} className="animate-spin" />} نعم، نفّذ
              </button>
            </div>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

function impactOfFlag(id, newState) {
  const map = {
    "marketplace.enabled": "يفتح Marketplace public endpoints — أي شخص يستطيع رؤية /api/marketplace/search.",
    "marketplace.discovery.enabled": "تظهر صفحة /m للعموم.",
    "marketplace.booking.enabled": "يمكن للعملاء حجز عبر POST /api/marketplace/bookings.",
    "marketplace.favorites.enabled": "يمكن للعملاء حفظ صالونات مفضّلة.",
    "marketplace.geo.enabled": "تفعيل البحث بالموقع (nearby) في الـ search.",
    "marketplace.reviews.enabled": "يسمح بإرسال تقييمات بعد الحجز.",
    "auth.google_oauth.enabled": "تفعيل تسجيل دخول بـ Google (يتطلب إعداد credentials).",
    "auth.mfa.enabled": "تفعيل MFA للموظفين.",
    "notifications.whatsapp.enabled": "تفعيل إرسال WhatsApp (يتطلب credentials في الإعدادات).",
  };
  return map[id] || "تغيير flag — تحقق من الأثر قبل المتابعة.";
}
